import { useCallback, useEffect, useRef, useState } from 'react'
import type * as Y from 'yjs'
import { allegatoPostaAmmesso, estensioneDi, tipoDichiarato, zipCifrato } from '@cidis/shared/allegati-posta'
import { ErroreApi } from '../../servizi/api.ts'
import * as posta from '../../servizi/posta.ts'
import * as bozze from '../../servizi/bozze.ts'
import { Bottone, BottoneIcona, Etichetta } from '../../componenti/base.tsx'
import { IconaChiudi, IconaGraffetta } from '../../componenti/Icone.tsx'
import '../../stile-posta-messaggio.css'

/**
 * Allegati del compositore (3/11/2026, dopo la segnalazione di Carmem: «quando caricano un allegato questo non va
 * insieme alla email»). Prima un file rifiutato dava un errore in cima alla finestra, che si perdeva, e il
 * messaggio partiva senza.
 *
 *  - Si allega tutto tranne i programmi (packages/shared/src/allegati-posta.ts, la stessa regola dell'API): il
 *    rifiuto si dice sulla riga degli allegati, accanto a «Allega un file», e resta finche' non lo si chiude.
 *  - Piu' file insieme: il selettore li accetta, e si trascinano nella finestra, come in Roundcube.
 *  - Ogni file ha il suo stato. In caricamento: «Invia» resta fermo e dice quale. Non caricato per un errore di
 *    rete o di Object Storage: si riprova o si toglie, e all'invio si chiede se spedire senza.
 *  - Uno .zip protetto da password: l'antivirus non lo apre, e lo si dice senza fermarlo.
 */

/** Gli allegati di un messaggio, come all'invio (rotte/webmail.ts, `Invio`). */
export const MASSIMO_ALLEGATI = 20

export type Caricamento = { id: string; nome: string; file: File; stato: 'in_corso' | 'non_caricato'; motivo?: string }
export type AvvisoAllegato = { id: string; testo: string; tono: 'errore' | 'attenzione' }

const nuovoId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`)

export function useCaricamenti({ doc, limiti, quanti, allCampo }: {
  doc: Y.Doc
  limiti: posta.LimitiAllegati
  /** Allegati gia' nel messaggio. */
  quanti: number
  /** La bozza condivisa dice agli altri che si sta allegando. */
  allCampo: () => void
}) {
  const [caricamenti, setCaricamenti] = useState<Caricamento[]>([])
  const [avvisi, setAvvisi] = useState<AvvisoAllegato[]>([])
  const inVolo = useRef(0)

  const avvisa = useCallback((testo: string, tono: AvvisoAllegato['tono'] = 'errore') =>
    setAvvisi((a) => [...a, { id: nuovoId(), testo, tono }]), [])

  const carica = useCallback(async (c: Caricamento) => {
    const mime = tipoDichiarato(c.file.type)
    try {
      const prenotazione = await posta.prenotaAllegato(c.file.name, mime, c.file.size)
      const caricato = await fetch(prenotazione.url_caricamento, {
        method: 'PUT',
        headers: { 'content-type': mime, 'x-amz-server-side-encryption': 'AES256' },
        body: c.file,
      })
      if (!caricato.ok) throw new Error(`il servizio dei file ha risposto ${caricato.status}`)
      doc.transact(() => bozze.allegatiDi(doc).push([{
        nome: c.file.name, mime, dimensione: c.file.size, storage_key: prenotazione.storage_key,
      }]), bozze.ORIGINE_LOCALE)
      setCaricamenti((l) => l.filter((x) => x.id !== c.id))
    } catch (e) {
      // Rifiutato dall'API (tipo, peso): non si riprova, si dice. Rete o Object Storage: si puo' riprovare.
      if (e instanceof ErroreApi && e.stato >= 400 && e.stato < 500) {
        setCaricamenti((l) => l.filter((x) => x.id !== c.id))
        avvisa(e.message)
      } else {
        const motivo = e instanceof ErroreApi ? e.message : (e as Error).message || 'connessione interrotta'
        setCaricamenti((l) => l.map((x) => (x.id === c.id ? { ...x, stato: 'non_caricato', motivo } : x)))
      }
    }
  }, [doc, avvisa])

  const allega = useCallback(async (files: File[]) => {
    allCampo()
    let posto = MASSIMO_ALLEGATI - quanti - inVolo.current
    for (const file of files) {
      const verdetto = allegatoPostaAmmesso(file.name, file.type)
      if (!verdetto.ammesso) { avvisa(verdetto.motivo); continue }
      if (file.size === 0) { avvisa(`«${file.name}» è vuoto: non c’è niente da allegare.`); continue }
      if (file.size > limiti.massimo_byte) {
        avvisa(`«${file.name}» supera ${posta.peso(limiti.massimo_byte)}, il massimo che il gestionale spedisce.`)
        continue
      }
      if (posto <= 0) { avvisa(`«${file.name}» non entra: al massimo ${MASSIMO_ALLEGATI} allegati per messaggio.`); continue }
      posto--
      if (estensioneDi(file.name) === 'zip') {
        const inizio = new Uint8Array(await file.slice(0, 30).arrayBuffer())
        if (zipCifrato(inizio)) {
          avvisa(`«${file.name}» è protetto da una password: l’antivirus non lo può aprire, e alcuni server di chi `
            + 'riceve lo rifiutano. Se puoi, mandalo senza password, o la password per un’altra via.', 'attenzione')
        }
      }
      const c: Caricamento = { id: nuovoId(), nome: file.name, file, stato: 'in_corso' }
      setCaricamenti((l) => [...l, c])
      inVolo.current++
      void carica(c).finally(() => { inVolo.current-- })
    }
  }, [quanti, limiti, avvisa, carica, allCampo])

  const riprova = useCallback((id: string) => {
    const c = caricamenti.find((x) => x.id === id)
    if (!c) return
    const di = { ...c, stato: 'in_corso' as const, motivo: undefined }
    setCaricamenti((l) => l.map((x) => (x.id === id ? di : x)))
    inVolo.current++
    void carica(di).finally(() => { inVolo.current-- })
  }, [caricamenti, carica])

  return {
    caricamenti, avvisi, allega, riprova,
    togli: (id: string) => setCaricamenti((l) => l.filter((x) => x.id !== id)),
    chiudiAvviso: (id: string) => setAvvisi((a) => a.filter((x) => x.id !== id)),
    inCorso: caricamenti.filter((c) => c.stato === 'in_corso').map((c) => c.nome),
    nonCaricati: caricamenti.filter((c) => c.stato === 'non_caricato').map((c) => c.nome),
  }
}

export type GestioneCaricamenti = ReturnType<typeof useCaricamenti>

/** La riga degli allegati: «Allega un file», gli allegati, i caricamenti e gli avvisi, che restano finche' si chiudono. */
export function RigaAllegati({ allegati, collegati, togliAllegato, bloccata, gestione }: {
  allegati: posta.AllegatoDaInviare[]
  /** Posizioni degli allegati che partiranno come collegamento (509). */
  collegati: Set<number>
  togliAllegato: (storageKey: string) => void
  bloccata: boolean
  gestione: GestioneCaricamenti
}) {
  return (
    <div className="posta-allegati-compositore">
      <div className="riga">
        {/* Il comando di allegato e' una <label>: il selettore dei file si apre dall'elemento nativo, nascosto. */}
        {!bloccata && (
          <label className="bottone bottone-secondario bottone-piccolo scelta-file">
            <IconaGraffetta /> Allega un file
            <input type="file" hidden multiple onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              if (files.length) void gestione.allega(files)
              e.target.value = ''
            }} />
          </label>
        )}
        {allegati.map((a, i) => (
          <Etichetta key={a.storage_key} tono={collegati.has(i) ? 'accento' : undefined}>
            {a.nome} <span className="tenue">{posta.peso(a.dimensione)}{collegati.has(i) ? ' · collegamento' : ''}</span>
            {!bloccata && (
              <BottoneIcona icona={<IconaChiudi />} etichetta={`Rimuovi l’allegato ${a.nome}`}
                            onClick={() => togliAllegato(a.storage_key)} />
            )}
          </Etichetta>
        ))}
        {gestione.caricamenti.map((c) => (
          c.stato === 'in_corso' ? (
            <Etichetta key={c.id}>
              {c.nome} <span className="tenue">caricamento…</span>
            </Etichetta>
          ) : (
            <Etichetta key={c.id} tono="critico">
              {c.nome} <span>· non caricato</span>
              <Bottone dimensione="piccolo" variante="discreto" onClick={() => gestione.riprova(c.id)}>Riprova</Bottone>
              <BottoneIcona icona={<IconaChiudi />} etichetta={`Togli ${c.nome}`} onClick={() => gestione.togli(c.id)} />
            </Etichetta>
          )
        ))}
        {!bloccata && !allegati.length && !gestione.caricamenti.length && (
          <span className="piccolo tenue solo-largo">oppure trascina qui i file</span>
        )}
      </div>
      {gestione.avvisi.length > 0 && (
        <div className="posta-allegati-avvisi">
          {gestione.avvisi.map((a) => (
            <div key={a.id} className={`posta-allegato-avviso posta-allegato-avviso-${a.tono}`} role="alert">
              <span>{a.testo}</span>
              <BottoneIcona icona={<IconaChiudi />} etichetta="Chiudi l’avviso" onClick={() => gestione.chiudiAvviso(a.id)} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * File trascinati nella finestra del compositore, come in Roundcube: si allegano dove li si lascia. Fuori dalla
 * finestra il browser non apre il file al posto del gestionale. Vero mentre si trascina, per il riquadro.
 */
export function useTrascinamento(attivo: boolean, suFile: (files: File[]) => void): boolean {
  const [sopra, setSopra] = useState(false)
  const conta = useRef(0)
  const ultimo = useRef(suFile)
  ultimo.current = suFile

  useEffect(() => {
    const conFile = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files')
    const entra = (e: DragEvent) => { if (!conFile(e)) return; e.preventDefault(); conta.current++; setSopra(true) }
    const passa = (e: DragEvent) => {
      if (!conFile(e)) return
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = attivo ? 'copy' : 'none'
    }
    const esce = (e: DragEvent) => {
      if (!conFile(e)) return
      conta.current = Math.max(0, conta.current - 1)
      if (!conta.current) setSopra(false)
    }
    const lascia = (e: DragEvent) => {
      if (!conFile(e)) return
      e.preventDefault()
      conta.current = 0
      setSopra(false)
      const files = Array.from(e.dataTransfer?.files ?? [])
      if (attivo && files.length) ultimo.current(files)
    }
    document.addEventListener('dragenter', entra)
    document.addEventListener('dragover', passa)
    document.addEventListener('dragleave', esce)
    document.addEventListener('drop', lascia)
    return () => {
      document.removeEventListener('dragenter', entra)
      document.removeEventListener('dragover', passa)
      document.removeEventListener('dragleave', esce)
      document.removeEventListener('drop', lascia)
    }
  }, [attivo])

  return sopra && attivo
}

/** Il riquadro sopra la finestra mentre si trascinano file. */
export function RilasciaFile() {
  return (
    <div className="posta-rilascia-file" aria-hidden="true">
      <IconaGraffetta /> Rilascia i file per allegarli
    </div>
  )
}
